import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardHeader from '@/components/Header/DashboardHeader';
import { Footer } from '@/components/organisms/Footer';
import { apiService } from '@/services/api';
import { CHAT_CART_ORDER_STATUS_LABELS, type ChatCartOrder } from '@/types/chatCartOrder';
import styles from './CartOrderListPage.module.scss';

export default function CartOrderListPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestId = searchParams.get('requestId') ?? undefined;
  const branchId = searchParams.get('branchId') ?? undefined;

  const [orders, setOrders] = useState<ChatCartOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiService.getCartOrders({ requestId, branchId }).then((res: any) => {
      const data = res?.data ?? res;
      const list = Array.isArray(data?.cartOrders) ? data.cartOrders : [];
      setOrders(list);
      setLoading(false);
    }).catch(() => {
      setError('Failed to load orders.');
      setLoading(false);
    });
  }, [requestId, branchId]);

  const handleBack = () => navigate(-1);
  const handleOpenOrder = (id: string) => navigate('/cart-orders/' + id);

  return (
    <>
      <DashboardHeader />
      <div className={styles.container}>
        <button type="button" className={styles.backButton} onClick={handleBack}>← Back</button>
        <h1 className={styles.title}>My cart orders</h1>
        {loading && <p>Loading orders...</p>}
        {error && <p className={styles.error}>{error}</p>}
        {!loading && !error && orders.length === 0 && (
          <p className={styles.empty}>No orders yet. Add items in the shop and share your cart in chat.</p>
        )}
        {!loading && !error && orders.length > 0 && (
          <ul className={styles.list}>
            {orders.map((order) => {
              const statusLabel = CHAT_CART_ORDER_STATUS_LABELS[order.status as keyof typeof CHAT_CART_ORDER_STATUS_LABELS] ?? order.status;
              const itemCount = (order.items ?? []).length;
              return (
                <li key={order.id} className={styles.card} onClick={() => handleOpenOrder(order.id)}>
                  <div className={styles.cardMeta}>
                    <span className={styles.statusBadge} data-status={order.status}>{statusLabel}</span>
                    <span className={styles.date}>{new Date(order.createdAt).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })}</span>
                  </div>
                  {order.branch?.name && <span className={styles.branch}>{order.branch.name}</span>}
                  <span className={styles.itemCount}>{itemCount} item{itemCount !== 1 ? 's' : ''}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <Footer />
    </>
  );
}
